// SuperChargeMe design tokens — single source of truth for the visual system.
// Light mode, RestockIQ-inspired. Consumed by muiTheme.js and any component
// that needs access to raw values.

export const colors = {
  // Primary — indigo (RestockIQ)
  primary: {
    50:  '#EEF2FF',
    100: '#E0E7FF',
    200: '#C7D2FE',
    300: '#A5B4FC',
    400: '#818CF8',
    500: '#6366F1',
    600: '#4F46E5',
    700: '#4338CA',
    800: '#3730A3',
    900: '#312E81',
    main: '#6366F1',
    dark: '#4F46E5',
    light: '#818CF8',
    contrastText: '#FFFFFF',
  },

  // Secondary — refined teal (subtle accent; used rarely)
  secondary: {
    50:  '#F0FDFA',
    500: '#14B8A6',
    600: '#0D9488',
    700: '#0F766E',
    main: '#0D9488',
    dark: '#0F766E',
    light: '#14B8A6',
    contrastText: '#FFFFFF',
  },

  // Semantic
  success: { main: '#10B981', hover: '#059669', bg: 'rgba(16, 185, 129, 0.08)',  border: 'rgba(16, 185, 129, 0.20)' },
  danger:  { main: '#EF4444', hover: '#DC2626', bg: 'rgba(239, 68, 68, 0.08)',   border: 'rgba(239, 68, 68, 0.20)' },
  warning: { main: '#F59E0B', hover: '#D97706', bg: 'rgba(245, 158, 11, 0.08)',  border: 'rgba(245, 158, 11, 0.20)' },
  info:    { main: '#3B82F6', hover: '#2563EB', bg: 'rgba(59, 130, 246, 0.08)',  border: 'rgba(59, 130, 246, 0.20)' },

  // Surfaces (light mode)
  bg: {
    base:     '#F7F8FA',                        // page background
    paper:    '#FFFFFF',                         // cards / surfaces
    subtle:   '#F1F2F5',                         // nested surfaces / code blocks
    input:    '#FAFAFB',                         // input fields
    hover:    'rgba(99, 102, 241, 0.06)',        // list-item hover (indigo tint)
    selected: 'rgba(99, 102, 241, 0.10)',        // list-item selected
    pressed:  'rgba(99, 102, 241, 0.14)',
    overlay:  'rgba(15, 15, 24, 0.35)',          // dialog backdrop
  },

  // Text
  text: {
    primary:   '#0F0F12',
    secondary: '#52525B',
    tertiary:  '#8E8E93',
    disabled:  '#A1A1AA',
    inverse:   '#FFFFFF',
    onPrimary: '#FFFFFF',
  },

  // Borders
  border: {
    default: '#E4E4E7',
    subtle:  '#F1F1F4',
    strong:  '#D4D4D8',
    divider: 'rgba(15, 15, 18, 0.06)',
    focus:   '#6366F1',
  },

  // Table helpers
  table: {
    headerBg:     '#F8FAFC',
    headerBorder: '#E2E8F0',
    cellBorder:   '#F1F5F9',
    rowHover:     'rgba(99, 102, 241, 0.04)',
  },

  // Tooltip
  tooltip: {
    bg:   '#1E293B',
    text: '#FFFFFF',
  },
};

export const typography = {
  fontFamily: {
    base:    '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif',
    display: '"Plus Jakarta Sans", "Inter", -apple-system, BlinkMacSystemFont, sans-serif',
    mono:    'source-code-pro, Menlo, Monaco, Consolas, "Courier New", monospace',
  },
  size: {
    xs:   '0.75rem',   // 12
    sm:   '0.8125rem', // 13
    base: '0.875rem',  // 14
    md:   '1rem',      // 16
    lg:   '1.125rem',  // 18
    xl:   '1.25rem',   // 20
    '2xl':'1.5rem',    // 24
    '3xl':'2rem',      // 32
    '4xl':'2.5rem',    // 40
  },
  weight: {
    regular:    400,
    medium:     500,
    semibold:   600,
    bold:       700,
    extrabold:  800,
  },
  lineHeight: {
    tight:   1.2,
    normal:  1.5,
    relaxed: 1.75,
  },
  letterSpacing: {
    tightest: '-0.025em',
    tight:    '-0.02em',
    normal:   '-0.01em',
    wide:     '0.01em',
    wider:    '0.05em',
  },
};

export const spacing = {
  xs:  4,
  sm:  8,
  md:  16,
  lg:  24,
  xl:  32,
  '2xl': 48,
  '3xl': 64,
};

export const radii = {
  sm:   6,
  md:   8,
  lg:   12,
  xl:   16,
  '2xl':20,
  pill: 9999,
};

export const shadows = {
  xs:        '0 1px 2px rgba(15, 15, 18, 0.04)',
  sm:        '0 1px 3px rgba(15, 15, 18, 0.04), 0 1px 2px rgba(15, 15, 18, 0.02)',
  md:        '0 4px 12px rgba(15, 15, 18, 0.06), 0 1px 3px rgba(15, 15, 18, 0.03)',
  lg:        '0 8px 24px rgba(15, 15, 18, 0.08), 0 2px 6px rgba(15, 15, 18, 0.04)',
  lift:      '0 12px 32px rgba(15, 15, 18, 0.10), 0 4px 12px rgba(15, 15, 18, 0.05)',
  liftHover: '0 20px 40px rgba(15, 15, 18, 0.14), 0 8px 20px rgba(15, 15, 18, 0.08)',
  dialog:    '0 24px 48px rgba(15, 15, 18, 0.12), 0 8px 24px rgba(15, 15, 18, 0.08)',
  focusRing: '0 0 0 3px rgba(99, 102, 241, 0.18)',
};

export const transitions = {
  fast:   '0.15s ease',
  base:   '0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  slow:   '0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  bounce: '0.4s cubic-bezier(0.16, 1, 0.3, 1)',
};

export const layout = {
  sidebarWidth:  240,
  headerHeight:  64,
  contentMaxW:   1440,
};

export const tokens = {
  colors,
  typography,
  spacing,
  radii,
  shadows,
  transitions,
  layout,
};

export default tokens;
